import { useSyncExternalStore } from "react";

/**
 * Factory for a tab-scoped store backed by sessionStorage.
 *
 * sessionStorage emits no events for same-tab writes, so every component
 * holding its own useState copy would drift the moment one of them wrote.
 * A store created here keeps the parsed value in a module-level cache and
 * notifies every subscriber on write, so all readers stay in sync.
 *
 * sessionStorage (not localStorage) so each tab keeps its own identity
 * alongside its own tokens — see utils/auth-storage.ts
 *
 * @author [Rajkumar Rathod]
 */

type Listener = () => void;

/** Every store registers its reset here so logout can drop all caches at once. */
const resetters = new Set<Listener>();

/**
 * Drops the in-memory cache of every session store. Call on logout, after
 * clearing storage — otherwise the next login's components read the
 * previous session's values.
 */
export const resetSessionStores = () => {
  resetters.forEach((reset) => reset());
};

export const createSessionStore = <T>(key: string, defaultValue: T) => {
  let cached: T = defaultValue;
  let hydrated = false;
  const listeners = new Set<Listener>();

  const emit = () => listeners.forEach((listener) => listener());

  const read = (): T => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw && raw !== "undefined" ? (JSON.parse(raw) as T) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  /**
   * getSnapshot must return a referentially stable value or React re-renders
   * forever, so parse once and serve the cache until a write invalidates it.
   */
  const getSnapshot = (): T => {
    if (!hydrated) {
      cached = read();
      hydrated = true;
    }
    return cached;
  };

  const subscribe = (listener: Listener) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const setValue = (v: T) => {
    // Clearing the value removes the key rather than storing "null"/"undefined",
    // so a later read falls back to defaultValue instead of a parsed literal.
    if (v === undefined || v === null) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, JSON.stringify(v));
    }
    cached = v;
    hydrated = true;
    emit();
  };

  const reset = () => {
    cached = defaultValue;
    hydrated = false;
    emit();
  };

  resetters.add(reset);

  const useStore = (): [T, (v: T) => void] => {
    const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    return [value, setValue];
  };

  return { useStore, getValue: getSnapshot, setValue, reset };
};
